import { option } from 'fp-ts';
import { AptunoError } from './error.model';

enum ResponseType {
  NOT_PERFORMED = 'NOT_PERFORMED',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

type ResponseShape = {
  type: ResponseType;
};

class NotPerformed implements ResponseShape {
  public readonly type = ResponseType.NOT_PERFORMED;
}

class Success<Result> implements ResponseShape {
  public readonly type = ResponseType.SUCCESS;

  constructor(public readonly result: Result) {}
}

class Failure implements ResponseShape {
  public readonly type = ResponseType.FAILURE;

  constructor(public readonly error: AptunoError | Error) {}
}

export type Response<R> = NotPerformed | Success<R> | Failure;

export const Response = {
  NotPerformed,
  Success,
  Failure,

  notPerformed: new NotPerformed(),

  isNotPerformed: <R>(response: Response<R>): response is NotPerformed => ResponseType.NOT_PERFORMED === response.type,

  isSucceed: <R>(response: Response<R>): response is Success<R> => ResponseType.SUCCESS === response.type,

  isError: <R>(response: Response<R>): response is Failure => ResponseType.FAILURE === response.type,

  asOption:
    <R, O>(f: (value: R) => O) =>
    (instance: Response<R>) =>
      Response.isSucceed(instance) ? option.some(f(instance.result)) : option.none,

  alt: <R>(a: Response<R>, b: Response<R>): Response<R> =>
    Response.isSucceed(a)
      ? a
      : Response.isSucceed(b)
      ? b
      : Response.isError(a)
      ? a
      : Response.isError(b)
      ? b
      : Response.notPerformed,
};
